/**
 * Mock data for generation history
 */

export interface GenerationRecord {
  id: string;
  timetableId: string;
  timetableName: string;
  profileUsed: string;
  profileName: string;
  startTime: Date;
  endTime: Date;
  duration: number; // seconds
  status: 'success' | 'partial' | 'failed';
  lessonsGenerated: number;
  conflictsFound: number;
  hardRuleViolations: number;
  softRuleViolations: number;
  curriculumCoverage: number;
  teacherSatisfaction: number;
  roomUtilization: number;
  createdBy: string;
  notes?: string;
}

export const mockGenerationHistory: GenerationRecord[] = [
  {
    id: 'gen-001',
    timetableId: 'timetable-current',
    timetableName: 'Autumn Term 2025 - Draft 3',
    profileUsed: 'profile-2',
    profileName: 'Balanced Optimization',
    startTime: new Date('2025-01-16T14:20:00Z'),
    endTime: new Date('2025-01-16T14:22:15Z'),
    duration: 135,
    status: 'success',
    lessonsGenerated: 252,
    conflictsFound: 8,
    hardRuleViolations: 0,
    softRuleViolations: 12,
    curriculumCoverage: 98.5,
    teacherSatisfaction: 87,
    roomUtilization: 92,
    createdBy: 'admin-1',
    notes: 'Best result so far with balanced profile',
  },
  {
    id: 'gen-002',
    timetableId: 'timetable-previous',
    timetableName: 'Autumn Term 2025 - Draft 2',
    profileUsed: 'profile-1',
    profileName: 'Strict Compliance',
    startTime: new Date('2025-01-14T16:40:00Z'),
    endTime: new Date('2025-01-14T16:45:32Z'),
    duration: 332,
    status: 'partial',
    lessonsGenerated: 245,
    conflictsFound: 15,
    hardRuleViolations: 0,
    softRuleViolations: 18,
    curriculumCoverage: 95.2,
    teacherSatisfaction: 82,
    roomUtilization: 85,
    createdBy: 'admin-1',
    notes: 'Took longer but fewer soft rule violations',
  },
  {
    id: 'gen-003',
    timetableId: 'timetable-draft-1',
    timetableName: 'Autumn Term 2025 - Draft 1',
    profileUsed: 'profile-3',
    profileName: 'Quick Generation',
    startTime: new Date('2025-01-13T10:15:00Z'),
    endTime: new Date('2025-01-13T10:16:05Z'),
    duration: 65,
    status: 'success',
    lessonsGenerated: 240,
    conflictsFound: 22,
    hardRuleViolations: 0,
    softRuleViolations: 28,
    curriculumCoverage: 92.8,
    teacherSatisfaction: 75,
    roomUtilization: 88,
    createdBy: 'admin-1',
    notes: 'Fast but many conflicts - used for initial testing',
  },
  {
    id: 'gen-004',
    timetableId: 'timetable-test',
    timetableName: 'Test Generation',
    profileUsed: 'profile-4',
    profileName: 'Teacher-Focused',
    startTime: new Date('2025-01-12T15:30:00Z'),
    endTime: new Date('2025-01-12T15:33:45Z'),
    duration: 225,
    status: 'success',
    lessonsGenerated: 248,
    conflictsFound: 12,
    hardRuleViolations: 0,
    softRuleViolations: 15,
    curriculumCoverage: 97.2,
    teacherSatisfaction: 94,
    roomUtilization: 78,
    createdBy: 'admin-1',
    notes: 'Excellent teacher satisfaction but lower room utilization',
  },
  {
    id: 'gen-005',
    timetableId: 'timetable-failed',
    timetableName: 'Failed Attempt',
    profileUsed: 'profile-1',
    profileName: 'Strict Compliance',
    startTime: new Date('2025-01-11T09:00:00Z'),
    endTime: new Date('2025-01-11T09:05:00Z'),
    duration: 300,
    status: 'failed',
    lessonsGenerated: 180,
    conflictsFound: 45,
    hardRuleViolations: 3,
    softRuleViolations: 35,
    curriculumCoverage: 72.5,
    teacherSatisfaction: 65,
    roomUtilization: 82,
    createdBy: 'admin-1',
    notes: 'Timed out - too many constraints, relaxed settings for subsequent attempts',
  },
];

export const getGenerationRecordsByStatus = (status: GenerationRecord['status']): GenerationRecord[] => {
  return mockGenerationHistory.filter((record) => record.status === status);
};

export const getGenerationRecordById = (id: string): GenerationRecord | undefined => {
  return mockGenerationHistory.find((record) => record.id === id);
};

export const getRecentGenerations = (limit: number): GenerationRecord[] => {
  return [...mockGenerationHistory].sort((a, b) => b.startTime.getTime() - a.startTime.getTime()).slice(0, limit);
};
